/**
 * AboutDetailsForm Component
 * Form for editing prospect about/contact details
 */

import { TextInput } from "../../../../../components/TextInput";
import { Dropdown } from "../../../../../components/Dropdown";
import type { DropdownOption } from "../../../../../components/Dropdown";
import { HandleInput } from "../../../../../components/HandleInput";
import {
  formatPhoneNumber,
  formatDateInput,
  stripHandlePrefix,
} from "../../../../../utils/formatters";
import {
  isJucoPlayerLevel,
  playerLevelFromLabel,
  PLAYER_LEVEL_DROPDOWN_OPTIONS,
} from "../../../../../constants/playerLevels";
import { JUCO_YEAR_DROPDOWN_OPTIONS } from "../../../../../constants/jucoYears";

export interface AboutFormData {
  phone: string;
  email: string;
  twitterUrl: string;
  instagramUrl: string;
  playerLevel: string;
  jucoYear: string;
  city: string;
  state: string;
  birthDate: string;
  visitDate: string;
  address: string;
  zipCode: string;
  parentName: string;
  parentPhone: string;
  coachName: string;
  coachPhone: string;
  advisorName: string;
  advisorPhone: string;
}

interface AboutDetailsFormProps {
  formData: AboutFormData;
  onFormChange: (data: Partial<AboutFormData>) => void;
  disabled: boolean;
}

const US_STATES: DropdownOption[] = [
  { label: "Alabama", value: "AL" },
  { label: "Alaska", value: "AK" },
  { label: "Arizona", value: "AZ" },
  { label: "Arkansas", value: "AR" },
  { label: "California", value: "CA" },
  { label: "Colorado", value: "CO" },
  { label: "Connecticut", value: "CT" },
  { label: "Delaware", value: "DE" },
  { label: "Florida", value: "FL" },
  { label: "Georgia", value: "GA" },
  { label: "Hawaii", value: "HI" },
  { label: "Idaho", value: "ID" },
  { label: "Illinois", value: "IL" },
  { label: "Indiana", value: "IN" },
  { label: "Iowa", value: "IA" },
  { label: "Kansas", value: "KS" },
  { label: "Kentucky", value: "KY" },
  { label: "Louisiana", value: "LA" },
  { label: "Maine", value: "ME" },
  { label: "Maryland", value: "MD" },
  { label: "Massachusetts", value: "MA" },
  { label: "Michigan", value: "MI" },
  { label: "Minnesota", value: "MN" },
  { label: "Mississippi", value: "MS" },
  { label: "Missouri", value: "MO" },
  { label: "Montana", value: "MT" },
  { label: "Nebraska", value: "NE" },
  { label: "Nevada", value: "NV" },
  { label: "New Hampshire", value: "NH" },
  { label: "New Jersey", value: "NJ" },
  { label: "New Mexico", value: "NM" },
  { label: "New York", value: "NY" },
  { label: "North Carolina", value: "NC" },
  { label: "North Dakota", value: "ND" },
  { label: "Ohio", value: "OH" },
  { label: "Oklahoma", value: "OK" },
  { label: "Oregon", value: "OR" },
  { label: "Pennsylvania", value: "PA" },
  { label: "Rhode Island", value: "RI" },
  { label: "South Carolina", value: "SC" },
  { label: "South Dakota", value: "SD" },
  { label: "Tennessee", value: "TN" },
  { label: "Texas", value: "TX" },
  { label: "Utah", value: "UT" },
  { label: "Vermont", value: "VT" },
  { label: "Virginia", value: "VA" },
  { label: "Washington", value: "WA" },
  { label: "West Virginia", value: "WV" },
  { label: "Wisconsin", value: "WI" },
  { label: "Wyoming", value: "WY" },
];

export function AboutDetailsForm({
  formData,
  onFormChange,
  disabled,
}: AboutDetailsFormProps) {
  return (
    <div className="prospect-viewer-panel__form-section">
      <TextInput
        label="Phone Number"
        type="tel"
        value={formData.phone}
        onChange={(e) => {
          const formatted = formatPhoneNumber(e.target.value);
          onFormChange({ phone: formatted });
        }}
        placeholder="(212) 456-7890"
        disabled={disabled}
      />

      <TextInput
        label="Email"
        type="email"
        value={formData.email}
        onChange={(e) => onFormChange({ email: e.target.value })}
        placeholder="drew.abercrombie@icloud.com"
        disabled={disabled}
      />

      <HandleInput
        variant="dashboard"
        label="X (Twitter)"
        value={stripHandlePrefix(formData.twitterUrl)}
        onChange={(value) => onFormChange({ twitterUrl: value })}
        placeholder="handle"
        disabled={disabled}
      />

      <HandleInput
        variant="dashboard"
        label="Instagram"
        value={stripHandlePrefix(formData.instagramUrl)}
        onChange={(value) => onFormChange({ instagramUrl: value })}
        placeholder="handle"
        disabled={disabled}
      />

      <Dropdown
        label="Player Level"
        options={PLAYER_LEVEL_DROPDOWN_OPTIONS}
        value={formData.playerLevel || null}
        onSelect={(value) => onFormChange({ playerLevel: value })}
        placeholder="Select level"
        disabled={disabled}
      />

      {/*
        Rendered only for a JUCO player rather than shown inert. `Dropdown` has no slot for hint
        text, so a disabled control here would sit there with nothing saying why - and a disabled
        control is out of the tab order, which puts that explanation furthest from the people most
        likely to need it. The add-prospect panel disables instead, because `SelectPopover` can
        carry the reason via `describedById`.

        Clearing the level clears the year on save without this field being touched:
        `levelNullFields` in services/prospects.ts adds `jucoYear` to `nullFields` whenever the
        level is not JUCO.
      */}
      {isJucoPlayerLevel(playerLevelFromLabel(formData.playerLevel)) && (
        <Dropdown
          label="JC Year"
          options={JUCO_YEAR_DROPDOWN_OPTIONS}
          value={formData.jucoYear || null}
          onSelect={(value) => onFormChange({ jucoYear: value })}
          placeholder="Select JC year"
          disabled={disabled}
        />
      )}

      <div className="prospect-viewer-panel__form-row">
        <TextInput
          label="City"
          value={formData.city}
          onChange={(e) => onFormChange({ city: e.target.value })}
          placeholder="New York"
          disabled={disabled}
        />
        <Dropdown
          label="State"
          options={US_STATES}
          value={formData.state || null}
          onSelect={(value) => onFormChange({ state: value })}
          placeholder="NY"
          disabled={disabled}
        />
      </div>

      <TextInput
        label="Birth Date"
        value={formData.birthDate}
        onChange={(e) => {
          const formatted = formatDateInput(e.target.value);
          onFormChange({ birthDate: formatted });
        }}
        placeholder="MM/DD/YYYY"
        disabled={disabled}
      />

      <TextInput
        label="Visit Date"
        value={formData.visitDate}
        onChange={(e) => {
          const formatted = formatDateInput(e.target.value);
          onFormChange({ visitDate: formatted });
        }}
        placeholder="MM/DD/YYYY"
        disabled={disabled}
      />

      <TextInput
        label="Address"
        value={formData.address}
        onChange={(e) => onFormChange({ address: e.target.value })}
        placeholder="Address"
        disabled={disabled}
      />

      <TextInput
        label="Zip Code"
        value={formData.zipCode}
        onChange={(e) => onFormChange({ zipCode: e.target.value })}
        placeholder="Zip Code"
        disabled={disabled}
      />

      <TextInput
        label="Parent Name"
        value={formData.parentName}
        onChange={(e) => onFormChange({ parentName: e.target.value })}
        placeholder="N/A"
        disabled={disabled}
      />

      <TextInput
        label="Parent Phone"
        type="tel"
        value={formData.parentPhone}
        onChange={(e) => {
          const formatted = formatPhoneNumber(e.target.value);
          onFormChange({ parentPhone: formatted });
        }}
        placeholder="(212) 456-7890"
        disabled={disabled}
      />

      <TextInput
        label="Coach Name"
        value={formData.coachName}
        onChange={(e) => onFormChange({ coachName: e.target.value })}
        placeholder="N/A"
        disabled={disabled}
      />

      <TextInput
        label="Coach Phone"
        type="tel"
        value={formData.coachPhone}
        onChange={(e) => {
          const formatted = formatPhoneNumber(e.target.value);
          onFormChange({ coachPhone: formatted });
        }}
        placeholder="(250) 888-8525"
        disabled={disabled}
      />

      <TextInput
        label="Advisor Name"
        value={formData.advisorName}
        onChange={(e) => onFormChange({ advisorName: e.target.value })}
        placeholder="Rich Marco"
        disabled={disabled}
      />

      <TextInput
        label="Advisor Phone"
        type="tel"
        value={formData.advisorPhone}
        onChange={(e) => {
          const formatted = formatPhoneNumber(e.target.value);
          onFormChange({ advisorPhone: formatted });
        }}
        placeholder="(250) 888-2589"
        disabled={disabled}
      />
    </div>
  );
}
